import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardChrome, WizardFooter } from '../../components/wizard/WizardChrome';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/SearchInput';
import { Select } from '../../components/ui/Select';
import { Checkbox, Pagination, Table, TD, TH, THead, TR } from '../../components/ui/Table';
import { AccordionCard } from '../../components/ui/AccordionCard';
import { Field, TextInput } from '../../components/ui/Field';
import { deviceMakeOptions, planShortLabel, schoolName, schools } from '../../data/district';
import { students } from '../../data/students';
import { usePagination } from '../../hooks/usePagination';
import { useToast } from '../../contexts/ToastContext';
const steps = ['Select Students', 'Add Devices'];
interface DeviceForm {
  make: string;
  model: string;
  serial: string;
  assetTag: string;
}
const emptyForm: DeviceForm = {
  make: '',
  model: '',
  serial: '',
  assetTag: ''
};
export function AddDevices() {
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [school, setSchool] = useState('All Schools');
  const [selected, setSelected] = useState<string[]>([]);
  const [forms, setForms] = useState<Record<string, DeviceForm>>({});
  const [openCard, setOpenCard] = useState<string | null>(null);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return students.filter(student => {
      if (school !== 'All Schools' && schoolName(student.schoolSlug) !== school) return false;
      if (!q) return true;
      return `${student.name} ${student.email} ${student.studentId}`.toLowerCase().includes(q);
    });
  }, [query, school]);
  const {
    page,
    setPage,
    totalPages,
    pageItems
  } = usePagination(rows, 25);
  const selectedStudents = students.filter(s => selected.includes(s.id));
  function toggle(id: string) {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  }
  function updateForm(id: string, patch: Partial<DeviceForm>) {
    setForms(prev => ({
      ...prev,
      [id]: {
        ...emptyForm,
        ...prev[id],
        ...patch
      }
    }));
  }
  function goToStep2() {
    setStep(2);
    setOpenCard(selected[0] ?? null);
  }
  return <>
      <WizardChrome title="Add Devices" steps={steps} currentStep={step} onClose={() => navigate('/overview')} data-id="element-702" data-mp="3m3e2zldloptf" />

      <div className="flex flex-1 flex-col bg-white px-6 py-5" data-id="element-703" data-mp="3r3a7dlq7mj9i">
        {step === 1 ? <>
            <h2 className="text-[15px] font-bold text-ink-900" data-id="element-704" data-mp="3d4aj4f6m06dv">Select Students</h2>
            <p className="mt-1 text-[13px] text-ink-500" data-id="element-705" data-mp="3i2u5uht11m7a">Select the students to assign devices to.</p>
            <p className="text-[13px] text-ink-500" data-id="element-706" data-mp="1bco1334p3ltj">Students with devices already assigned will be grayed out.</p>

            <div className="mt-4 flex flex-wrap items-center gap-2" data-id="element-707" data-mp="1g9xoyecfrpf9">
              <Select options={['All Schools', ...schools.map(s => s.name)]} value={school} placeholder="All Schools" onChange={setSchool} data-id="element-708" data-mp="1qdl5usx77lbh" />
              <SearchInput value={query} onChange={setQuery} placeholder="Search students by name, email or device ID" className="w-[235px]" data-id="element-709" data-mp="1z7hklmo19vgv" />
            </div>

            <div className="mt-4 flex flex-1 flex-col" data-id="element-710" data-mp="296ox67my1m2x">
              <Table minWidth={1240} data-id="element-711" data-mp="244zmwu7jpcfw">
                <THead data-id="element-712" data-mp="24859o2spc28g">
                  <tr data-id="element-713" data-mp="2i62bpepxd0ui">
                    <TH width={44} data-id="element-714" data-mp="2i4ir5bx8k3mx" />
                    <TH data-id="element-715" data-mp="2i3g279jh4b9n">Student Name</TH>
                    <TH data-id="element-716" data-mp="2e3fboqj3o9m8">Student Email</TH>
                    <TH data-id="element-717" data-mp="2e4qhsx00ptgi">Student ID</TH>
                    <TH data-id="element-718" data-mp="nr9h9j0s2g45">Guardian Name</TH>
                    <TH data-id="element-719" data-mp="nsau9t1ctcoi">Guardian Email</TH>
                    <TH data-id="element-720" data-mp="ntcdwlwfr7hu">School</TH>
                    <TH data-id="element-721" data-mp="jqpm2kzrlmkn">Plan</TH>
                    <TH data-id="element-722" data-mp="jrilt20b3bta">Plan Purchase Date</TH>
                  </tr>
                </THead>
                <tbody data-id="element-723" data-mp="l6l8vrfcl54l">
                  {pageItems.map(student => {
                const disabled = student.hasDevice;
                const checked = selected.includes(student.id);
                return <TR key={student.id} disabled={disabled} selected={checked} onClick={disabled ? undefined : () => toggle(student.id)} data-id="element-724" data-mp="u5yjinf7jzqw">
                        <TD data-id="element-725" data-mp="6j065ymdx7x7">
                          <Checkbox checked={checked} disabled={disabled} onChange={() => toggle(student.id)} label={`Select ${student.name}`} data-id="element-726" data-mp="6g49seq6qrav" />
                        </TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-727" data-mp="3ew60gdp98tzf">{student.name}</TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-728" data-mp="3jv0i3cbhkchy">{student.email}</TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-729" data-mp="3ccsv2jpg4mpx">{student.studentId}</TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-730" data-mp="37fak6nrvbxfa">{student.guardianName}</TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-731" data-mp="32dlabbgdtpss">{student.guardianEmail}</TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-732" data-mp="2ydjj5w89werr">{schoolName(student.schoolSlug)}</TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-733" data-mp="2tgt1j4gtlb48">
                          {planShortLabel(student.planId)}
                        </TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-734" data-mp="2jpye3wru85h7">{student.planPurchaseDate}</TD>
                      </TR>;
              })}
                </tbody>
              </Table>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} data-id="element-735" data-mp="2amxca8a090jc" />
            </div>
          </> : <>
            <h2 className="text-[15px] font-bold text-ink-900" data-id="element-736" data-mp="1w1jf01tjvn1y">Add Devices</h2>
            <p className="mt-1 text-[13px] text-ink-500" data-id="element-737" data-mp="1w3mf5eof2wke">
              Provide the device details for each selected student.
            </p>

            <div className="mt-4 space-y-3" data-id="element-738" data-mp="1m3vhx58h23gm">
              {selectedStudents.map(student => {
            const form = forms[student.id] ?? emptyForm;
            return <AccordionCard key={student.id} title={student.name} subtitle={`${planShortLabel(student.planId)} • ${schoolName(student.schoolSlug)}`} open={openCard === student.id} onToggle={() => setOpenCard(openCard === student.id ? null : student.id)} sectionLabel="Device Details" data-id="element-739" data-mp="2es9jfuyrrhwx">
                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-740" data-mp="2xpu8b21jaghj">
                      <Field label="Device Make" htmlFor={`make-${student.id}`} data-id="element-741" data-mp="32hvh2dzhtp2t">
                        <Select id={`make-${student.id}`} fullWidth value={form.make} onChange={value => updateForm(student.id, {
                    make: value
                  })} options={deviceMakeOptions} placeholder="Enter Device Make" data-id="element-742" data-mp="r2d4u58gnc16" />
                      </Field>
                      <Field label="Device Model" htmlFor={`model-${student.id}`} data-id="element-743" data-mp="82z679zsdd4c">
                        <TextInput id={`model-${student.id}`} value={form.model} onChange={value => updateForm(student.id, {
                    model: value
                  })} placeholder="Enter Device Model" data-id="element-744" data-mp="34xp2ulx7nvi" />
                      </Field>
                      <Field label="Serial Number" htmlFor={`serial-${student.id}`} data-id="element-745" data-mp="1siasmhm7l7vk">
                        <TextInput id={`serial-${student.id}`} value={form.serial} onChange={value => updateForm(student.id, {
                    serial: value
                  })} placeholder="Enter Serial Number" data-id="element-746" data-mp="1njhh2uqhfqy1" />
                      </Field>
                      <Field label="Asset Tag ID" htmlFor={`asset-${student.id}`} data-id="element-747" data-mp="2gl7gr6d78njm">
                        <TextInput id={`asset-${student.id}`} value={form.assetTag} onChange={value => updateForm(student.id, {
                    assetTag: value
                  })} placeholder="Enter Asset Tag ID" data-id="element-748" data-mp="2lflhloamrmvo" />
                      </Field>
                    </div>
                  </AccordionCard>;
          })}
            </div>
          </>}
      </div>

      <WizardFooter data-id="element-749" data-mp="15yl9p4pg6qt0">
        {step === 2 && <Button variant="secondary" onClick={() => setStep(1)} data-id="element-750" data-mp="19yvg2ih5jj8e">
            Back
          </Button>}
        {step === 1 ? <Button disabled={selected.length === 0} onClick={goToStep2} data-id="element-751" data-mp="12ge2a028ti9m">
            Continue
          </Button> : <Button onClick={() => {
        navigate('/overview');
        showToast('Devices Added', 'Devices have been added successfully');
      }} data-id="element-752" data-mp="xn2pliar279s">
            Add Devices
          </Button>}
      </WizardFooter>
    </>;
}